import {
    Show,
    Edit,
    SimpleForm,
    TextField,
    SelectField,
    SelectInput,
    TextInput,
    required,
    maxLength,
    usePermissions,
    FunctionField,
    useRecordContext,
    TopToolbar,
    useTranslate,
    DateField,
} from 'react-admin';
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import { OnlyBulkDeleteActionButton } from '@/components/global/buttons/bulkButtons.jsx';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import CustomExportButton from '@/components/global/buttons/CustomExportButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { useDrawer } from '@/composables/useDrawer';
import { RightDrawer } from '@/components/global/drawers/RightDrawer';
import { ChangeHistory } from '@/components/global/history/ChangeHistory.jsx';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';
import { softwareTypeChoices } from '@/components/global/options/selectOptions.jsx';
import ImportFileInput from '@/components/global/inputs/ImportFileInput.jsx';
import { DetailsSampleList } from '@/components/global/fields/DetailsSampleList.jsx';

const searchBar = <SearchBar source='value' labelKey='resources.software.filters.search' alwaysOn />;

const getFilters = (isMobile, translate) => [
    searchBar,
    <SelectInput
        key='type'
        source='type'
        label={translate('resources.software.filters.type')}
        choices={softwareTypeChoices}
        validate={required(translate('common.validation.required'))}
        alwaysOn={isMobile}
    />,
];

const MoreActionsPopover = ({ permissions }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { openDrawer } = useDrawer();
    if (!record) return null;

    return (
        <>
            <CustomMorePopover permissions={permissions} record={record}>
                <CustomShowModalButton label={translate('resources.software.labels.showSoftware')} onOpen={() => openDrawer(record.id, 'software-show')} />
                {permissions.isAdmin && (
                    <CustomEditModalButton label={translate('resources.software.labels.editSoftware')} onOpen={() => openDrawer(record.id, 'software-edit')} />
                )}
            </CustomMorePopover>
        </>
    );
};

const ListActions = () => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    if (isLoading) return null;

    return (
        <TopToolbar>
            <SearchFilterInput />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} />
            {(permissions.isAdmin || permissions.isServiceMaster) && (
                <CustomToolbarActionsPopover>
                    <CustomCreateButton label={translate('resources.software.labels.createSoftware')} />
                    <CustomExportButton label={translate('common.exportCsv')} />
                </CustomToolbarActionsPopover>
            )}
        </TopToolbar>
    );
};

export const SoftwareList = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const translate = useTranslate();
    const filters = useMemo(() => getFilters(isMobile, translate), [isMobile, translate]);

    if (isLoading) return null;

    return (
        <CustomList filters={filters} actions={<ListActions />}>
            <CustomDatagridConfigurable bulkActionButtons={permissions.isAdmin ? <OnlyBulkDeleteActionButton /> : false} rowClick={false}>
                <TextField source='version' label={translate('resources.software.fields.version')} />
                <SelectField source='type' choices={softwareTypeChoices} label={translate('resources.software.fields.type')} />
                <TextField source='description' label={translate('common.description')} />
                <FunctionField
                    source='createdAt'
                    sortBy='createdAt'
                    label={translate('resources.software.fields.createdAt')}
                    render={record =>
                        record?.createdAt ? (
                            <Tooltip
                                title={
                                    formatIntervalSince(record.createdAt) ? `${formatIntervalSince(record.createdAt)} ${translate('notification.timeAgo')}` : ''
                                }
                                arrow
                                classes={{ tooltip: 'tooltip-black' }}
                            >
                                <span>{format(new Date(record.createdAt), 'dd.MM.yyyy HH:mm')}</span>
                            </Tooltip>
                        ) : (
                            ''
                        )
                    }
                />
                <FunctionField
                    source='updatedAt'
                    sortBy='updatedAt'
                    label={translate('resources.software.fields.lastUpdatedAt')}
                    render={record =>
                        record?.updatedAt ? (
                            <Tooltip
                                title={
                                    formatIntervalSince(record.updatedAt) ? `${formatIntervalSince(record.updatedAt)} ${translate('notification.timeAgo')}` : ''
                                }
                                arrow
                                classes={{ tooltip: 'tooltip-black' }}
                            >
                                <span>{format(new Date(record.updatedAt), 'dd.MM.yyyy HH:mm')}</span>
                            </Tooltip>
                        ) : (
                            ''
                        )
                    }
                />
                <FunctionField label={translate('common.options')} render={record => <MoreActionsPopover permissions={permissions} groupId={record.id} />} />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};

export const SoftwareCreate = ({ handleClose }) => {
    const translate = useTranslate();

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.software.labels.createSoftware')}>
            <CustomCreate handleClose={handleClose}>
                <SimpleForm>
                    <TextInput
                        source='version'
                        label={translate('resources.software.fields.version')}
                        validate={[required(translate('common.validation.required')), maxLength(30, translate('common.validation.maxLength', { count: 30 }))]}
                        fullWidth
                    />
                    <SelectInput
                        source='type'
                        label={translate('resources.software.fields.type')}
                        choices={softwareTypeChoices}
                        validate={[required(translate('common.validation.required'))]}
                        fullWidth
                    />
                    <TextInput
                        source='description'
                        label={translate('common.description')}
                        validate={[required(translate('common.validation.required')), maxLength(200, translate('common.validation.maxLength', { count: 200 }))]}
                        fullWidth
                    />
                    <ImportFileInput
                        source='file'
                        validate={[required(translate('common.validation.required'))]}
                        placeholderText={translate('resources.software.labels.uploadPlaceholder')}
                        accept=''
                        fullWidth
                    />
                </SimpleForm>
            </CustomCreate>
        </RightDrawer>
    );
};

export const SoftwareShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <RightDrawer
            handleClose={handleClose}
            title={translate('common.details')}
            after={<ChangeHistory resource='software' recordId={elementId} fieldsSource='resources.software.fields' />}
        >
            <Show
                id={elementId}
                resource='software'
                actions={false}
                toolbar={false}
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <DetailsSampleList>
                    <TextField source='id' label={translate('common.name')} />
                    <TextField source='version' label={translate('resources.software.fields.version')} />
                    <SelectField source='type' choices={softwareTypeChoices} label={translate('resources.software.fields.type')} />
                    <TextField source='description' label={translate('common.description')} />
                    <DateField source='createdAt' showTime label={translate('resources.software.fields.createdAt')} />
                    <DateField source='updatedAt' showTime label={translate('resources.software.fields.lastUpdatedAt')} />
                </DetailsSampleList>
            </Show>
        </RightDrawer>
    );
};

SoftwareShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const includeSoftwareType = (data, { previousData } = {}) => ({
    ...data,
    type: data?.type ?? previousData?.type,
});

export const SoftwareEdit = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.software.labels.editSoftware')}>
            <Edit
                mutationMode='pessimistic'
                id={elementId}
                actions={false}
                transform={includeSoftwareType}
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <SimpleForm>
                    <TextInput
                        source='version'
                        label={translate('resources.software.fields.version')}
                        validate={[required(translate('common.validation.required')), maxLength(30, translate('common.validation.maxLength', { count: 30 }))]}
                        fullWidth
                    />
                    <SelectInput
                        source='type'
                        label={translate('resources.software.fields.type')}
                        choices={softwareTypeChoices}
                        validate={[required(translate('common.validation.required'))]}
                        readOnly
                        fullWidth
                    />
                    <TextInput
                        source='description'
                        label={translate('common.description')}
                        validate={[required(translate('common.validation.required')), maxLength(200, translate('common.validation.maxLength', { count: 200 }))]}
                        fullWidth
                    />
                    <ImportFileInput source='file' placeholderText={translate('resources.software.labels.uploadPlaceholderEdit')} accept='' fullWidth />
                </SimpleForm>
            </Edit>
        </RightDrawer>
    );
};

SoftwareEdit.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
